/**
 * Producer Training: Go Live with the Overlay
 * Demonstrates Go Live/Dark and each overlay control toggle.
 * Large OBS preview shows the effect of each control in real time.
 * 1200 frames @ 30fps = 40 seconds.
 *
 * Sequence: dark→live→dark→live, then:
 *   Show Heat Name: on, off, on
 *   Show Leaderboard: on, off
 *   Show Riders: on, off, on
 *   Show Scores: on, off, on
 */

import { AbsoluteFill, Sequence, useCurrentFrame, interpolate } from "remotion";
import { AutoCursor } from "../../components/AutoCursor";
import { ScreenMock } from "../../components/ScreenMock";
import { FadeTransition } from "../../components/FadeTransition";
import { StepLabel } from "../../components/StepLabel";
import { ThemedOverlay } from "@/overlay/ThemedOverlay";
import { OVERLAY_STYLES } from "@/overlay/styles";

const ACCENT = "#3b82f6";

export function GoLiveGuide() {
  return (
    <AbsoluteFill style={{ backgroundColor: "#0a0a0b", fontFamily: "'Inter', sans-serif" }}>
      <Sequence durationInFrames={65}><Title /></Sequence>
      <Sequence from={55} durationInFrames={1085}>
        <FadeTransition durationInFrames={1085} fadeIn={10} fadeOut={10}><Demo /></FadeTransition>
      </Sequence>
      <Sequence from={1130} durationInFrames={70}><Done /></Sequence>
    </AbsoluteFill>
  );
}

/* ── Title / Done ── */

function Title() {
  const frame = useCurrentFrame();
  const op = interpolate(frame, [0, 12, 48, 65], [0, 1, 1, 0], { extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ justifyContent: "center", alignItems: "center", opacity: op }}>
      <div style={{ fontSize: 10, color: "#22c55e", textTransform: "uppercase", letterSpacing: 3, fontFamily: mono, marginBottom: 14 }}>Producer Training</div>
      <div style={{ fontSize: 52, fontWeight: 800, color: "#fff" }}>Go Live with the Overlay</div>
    </AbsoluteFill>
  );
}

function Done() {
  const frame = useCurrentFrame();
  const op = interpolate(frame, [0, 12], [0, 1], { extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ justifyContent: "center", alignItems: "center", opacity: op }}>
      <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#22c55e", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
        <span style={{ fontSize: 24, color: "#fff" }}>&#10003;</span>
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: "#fff" }}>You control the broadcast</div>
    </AbsoluteFill>
  );
}

/* ── Demo ── */

function Demo() {
  const frame = useCurrentFrame();

  /* State derived from cursor click frames */
  const isLive = (frame >= 30 && frame < 110) || frame >= 180;
  const showHeatName = (frame >= 255 && frame < 335) || frame >= 405;
  const showLeaderboard = frame >= 480 && frame < 560;
  const showRiders = (frame >= 640 && frame < 720) || frame >= 790;
  const showScores = (frame >= 870 && frame < 950) || frame >= 1020;

  return (
    <AbsoluteFill>
      <ScreenMock url="raley.koda-software.com/competition/boardstock/producer">
        <ProducerMock
          isLive={isLive}
          showHeatName={showHeatName}
          showLeaderboard={showLeaderboard}
          showRiders={showRiders}
          showScores={showScores}
        />
        <AutoCursor points={CURSOR_POINTS} />
      </ScreenMock>
      <StepLabel text="Go Live starts the broadcast" show={10} hide={100} color="#22c55e" />
      <StepLabel text="Go Dark stops the broadcast" show={100} hide={170} color="#ef4444" />
      <StepLabel text="Go Live resumes broadcasting" show={170} hide={240} color="#22c55e" />
      <StepLabel text="Show Heat Name — toggles the heat title on the overlay" show={240} hide={465} color={ACCENT} />
      <StepLabel text="Show Leaderboard — displays rankings on the overlay" show={465} hide={620} color={ACCENT} />
      <StepLabel text="Show Riders — displays rider cards on the overlay" show={620} hide={850} color={ACCENT} />
      <StepLabel text="Show Scores — displays individual scores on rider cards" show={850} hide={1075} color={ACCENT} />
    </AbsoluteFill>
  );
}

const CURSOR_POINTS = [
  { x: 960, y: 300, frame: 0 },
  // Go Live
  { target: "go-live-btn", frame: 20 },
  { target: "go-live-btn", frame: 30, click: true },
  { target: "go-live-btn", frame: 100 },
  // Go Dark
  { target: "go-live-btn", frame: 110, click: true },
  { target: "go-live-btn", frame: 170 },
  // Go Live again
  { target: "go-live-btn", frame: 180, click: true },
  // Show Heat Name: on, off, on
  { target: "ctrl-heat-name", frame: 245 },
  { target: "ctrl-heat-name", frame: 255, click: true },
  { target: "ctrl-heat-name", frame: 325 },
  { target: "ctrl-heat-name", frame: 335, click: true },
  { target: "ctrl-heat-name", frame: 395 },
  { target: "ctrl-heat-name", frame: 405, click: true },
  // Show Leaderboard: on, off
  { target: "ctrl-leaderboard", frame: 470 },
  { target: "ctrl-leaderboard", frame: 480, click: true },
  { target: "ctrl-leaderboard", frame: 550 },
  { target: "ctrl-leaderboard", frame: 560, click: true },
  // Show Riders: on, off, on
  { target: "ctrl-riders", frame: 630 },
  { target: "ctrl-riders", frame: 640, click: true },
  { target: "ctrl-riders", frame: 710 },
  { target: "ctrl-riders", frame: 720, click: true },
  { target: "ctrl-riders", frame: 780 },
  { target: "ctrl-riders", frame: 790, click: true },
  // Show Scores: on, off, on
  { target: "ctrl-scores", frame: 860 },
  { target: "ctrl-scores", frame: 870, click: true },
  { target: "ctrl-scores", frame: 940 },
  { target: "ctrl-scores", frame: 950, click: true },
  { target: "ctrl-scores", frame: 1010 },
  { target: "ctrl-scores", frame: 1020, click: true },
  { target: "ctrl-scores", frame: 1075 },
];

/* ── Producer Mock ── */

function ProducerMock({
  isLive,
  showHeatName,
  showLeaderboard,
  showRiders,
  showScores,
}: {
  isLive: boolean;
  showHeatName: boolean;
  showLeaderboard: boolean;
  showRiders: boolean;
  showScores: boolean;
}) {
  return (
    <div style={shell}>
      {/* Topbar */}
      <div style={topbarStyle}>
        <span style={brand}>RALEY</span>
        <span style={{ ...navLinkStyle, color: "#e4e4e7" }}>Competitions</span>
        <span style={navLinkStyle}>Riders</span>
        <span style={navLinkStyle}>Training</span>
        <span style={{ marginLeft: "auto", fontFamily: mono, fontSize: 11, color: "#71717a" }}>sarah@email.com</span>
      </div>

      <div style={{ padding: "16px 24px", flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Page header — matches real Producer page */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 20, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1 }}>Sarah</div>
          <div style={{ fontSize: 12, color: "#71717a", marginTop: 2 }}>Boardstock Invitational</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
            {isLive ? (
              <span style={{ padding: "2px 8px", fontSize: 10, fontWeight: 700, textTransform: "uppercase", background: "#ef4444", color: "#fff" }}>LIVE</span>
            ) : (
              <span style={{ padding: "2px 8px", fontSize: 10, fontWeight: 700, textTransform: "uppercase", background: "#2a2a32", color: "#71717a" }}>OFF AIR</span>
            )}
            <div style={{
              padding: "4px 12px", fontSize: 12, background: "#111113",
              border: "1px solid #2a2a32", color: "#e4e4e7", minWidth: 180,
              display: "flex", alignItems: "center", justifyContent: "space-between",
              borderRadius: 6,
            }}>
              Finals — Men&apos;s Pro
              <span style={{ fontSize: 8, color: "#71717a", marginLeft: 8 }}>▼</span>
            </div>
            <div style={{
              padding: "4px 10px", fontSize: 11, fontWeight: 600, background: "#1a1a1e",
              color: "#e4e4e7", border: "1px solid #2a2a32", borderRadius: 6,
            }}>
              Edit Heats
            </div>
            {isLive ? (
              <div id="go-live-btn" style={{
                padding: "4px 10px", background: "transparent", color: "#ef4444",
                border: "1px solid #ef4444", fontSize: 11, fontWeight: 600,
                textTransform: "uppercase", borderRadius: 6, cursor: "pointer",
              }}>
                Go Dark
              </div>
            ) : (
              <div id="go-live-btn" style={{
                padding: "4px 10px", background: ACCENT, color: "#fff",
                fontSize: 11, fontWeight: 600, textTransform: "uppercase",
                borderRadius: 6, cursor: "pointer",
              }}>
                Go Live
              </div>
            )}
          </div>
        </div>

        {/* Overlay controls — exact labels from real page */}
        <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
          <CtrlBtn id="ctrl-heat-name" on={showHeatName}>
            {showHeatName ? "Hide Heat Name" : "Show Heat Name"}
          </CtrlBtn>
          <CtrlBtn id="ctrl-leaderboard" on={showLeaderboard}>
            {showLeaderboard ? "Hide Heat Leaderboard" : "Show Heat Leaderboard"}
          </CtrlBtn>
          <CtrlBtn id="ctrl-riders" on={showRiders}>
            {showRiders ? "Hide Heat Riders" : "Show Heat Riders"}
          </CtrlBtn>
          <CtrlBtn id="ctrl-scores" on={showScores}>
            {showScores ? "Hide Rider Scores" : "Show Rider Scores"}
          </CtrlBtn>
          <CtrlBtn on={false}>Clear Focus</CtrlBtn>
        </div>

        {/* OBS Preview — same pattern as real OBS preview component */}
        <div style={{
          width: 1920 * 0.65, height: 1080 * 0.65,
          margin: "0 auto",
          position: "relative", overflow: "hidden",
          border: "1px solid #2a2a32", borderRadius: 6,
          background: isLive ? "linear-gradient(135deg, #0d1b2a 0%, #1a3a5c 50%, #0d1b2a 100%)" : "#0a0a0b",
        }}>
          {isLive ? (
            <div style={{
              width: 1920, height: 1080,
              transform: "scale(0.65)",
              transformOrigin: "top left",
            }}>
              <ThemedOverlay
                theme={OVERLAY_STYLES.steel}
                heatName="Finals — Men's Pro"
                competitionName="Boardstock Invitational"
                riders={MOCK_RIDERS}
                showHeatBanner={showHeatName}
                showLeaderboard={showLeaderboard}
                showHeatScores={showRiders}
                showRiderScores={showScores}
                judgeCount={3}
                allScores={MOCK_SCORES}
                isFreeTier={false}
              />
            </div>
          ) : (
            <div style={{
              width: "100%", height: "100%", display: "flex", alignItems: "center",
              justifyContent: "center", background: "#0a0a0b",
            }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#2a2a32", textTransform: "uppercase", letterSpacing: 2, fontFamily: mono }}>
                OFF AIR
              </div>
            </div>
          )}
          <div style={{
            position: "absolute", top: 8, right: 8,
            fontSize: 9, color: "#52525b", fontFamily: mono,
            background: "rgba(0,0,0,0.6)", padding: "2px 8px",
            textTransform: "uppercase", letterSpacing: 0.5, zIndex: 10,
          }}>
            OBS Preview
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Control Button ── */

function CtrlBtn({ id, on, children }: { id?: string; on: boolean; children: React.ReactNode }) {
  return (
    <div id={id} style={{
      padding: "4px 10px", fontSize: 10, fontWeight: 600, textTransform: "uppercase",
      background: on ? ACCENT : "#1a1a1e",
      color: on ? "#fff" : "#e4e4e7",
      border: `1px solid ${on ? ACCENT : "#2a2a32"}`,
      borderRadius: 6, cursor: "pointer", whiteSpace: "nowrap",
    }}>
      {children}
    </div>
  );
}

/* ── Mock data for real ThemedOverlay ── */

const MOCK_RIDERS = [
  { id: "1", name: "Harley Clifford", initials: "HC", colour: "#3b82f6", score: 9.2, hits: 0, nickname: "The King", number: "1", currentAttempt: 3, maxAttempts: 3, attemptScores: { "1": 8.7, "2": 9.2, "3": 8.1 } },
  { id: "2", name: "Nic Rapa", initials: "NR", colour: "#22c55e", score: 8.4, hits: 0, nickname: "Raptor", number: "7", currentAttempt: 3, maxAttempts: 3, attemptScores: { "1": 7.2, "2": 8.4, "3": 7.8 } },
  { id: "3", name: "Dallas Friday", initials: "DF", colour: "#eab308", score: 9.1, hits: 0, nickname: "D-Fri", number: "12", currentAttempt: 3, maxAttempts: 3, attemptScores: { "1": 9.0, "2": 8.8, "3": 9.1 } },
  { id: "4", name: "Amber Wing", initials: "AW", colour: "#ef4444", score: 7.1, hits: 0, nickname: "Wings", number: "3", currentAttempt: 2, maxAttempts: 3, attemptScores: { "1": 6.5, "2": 7.1 } as Record<string, number | null> },
  { id: "5", name: "Steel Lafferty", initials: "SL", colour: "#a855f7", score: 8.3, hits: 0, nickname: "Steele", number: "22", currentAttempt: 3, maxAttempts: 3, attemptScores: { "1": 8.1, "2": 7.5, "3": 8.3 } },
];

const MOCK_SCORES: Record<string, Record<string, number>> = {
  "1": { "1": 9.0, "2": 9.5, "3": 9.0 },
  "2": { "1": 8.0, "2": 8.5, "3": 8.8 },
  "3": { "1": 9.2, "2": 8.8, "3": 9.3 },
  "4": { "1": 7.0, "2": 7.2, "3": 7.1 },
  "5": { "1": 8.0, "2": 8.5, "3": 8.5 },
};

/* ── Styles ── */

const mono = "'JetBrains Mono', monospace";

const shell: React.CSSProperties = {
  width: "100%", height: "100%", background: "#0a0a0b",
  fontFamily: "'Inter', sans-serif", color: "#e4e4e7",
  display: "flex", flexDirection: "column",
};

const topbarStyle: React.CSSProperties = {
  height: 40, background: "#111113", borderBottom: "1px solid #2a2a32",
  display: "flex", alignItems: "center", gap: 24, padding: "0 24px", flexShrink: 0,
};

const brand: React.CSSProperties = {
  fontFamily: mono, fontWeight: 800, fontSize: 14, letterSpacing: 2,
};

const navLinkStyle: React.CSSProperties = {
  fontSize: 12, fontWeight: 500, color: "#52525b",
};
